// authSlice.js
import { createSlice } from '@reduxjs/toolkit';

import { login, guestLogin, getLivekitUrl, getTenantByDomain, getTenantSettings, guestLoginDemo, logoutGuestUser, getUserPermissions, getGuestDemoLinkEncryptedSetting, getGuestDemoLinkValidationSetting } from './authThunks';
import socketFactory from '../../../utils/socketFactory';
import config from '../../../constants/config';
import { is } from 'date-fns/locale';
import { bindTenantSettings } from '../utils/AuthUtils';
import { set } from 'date-fns';
import { setOpen } from '../../ControlPanel/store/AddEvent/AddEventSlice';

const initialState = {
  logoutDialogOpen: false,
  isAuthenticated: false,
  keycloakSettings: null,
  localAvatar: null,
  livekitUrl: null,
  tenant: null,
  tenantPublic: null,
  token: null,
  error: null,
  preferredLanguage: localStorage.getItem('language') || 'fr',
  doubleSession: false,
  invitationAuthorization: true,
  isGuest: false,
  isGuestDemo: false,
  permissions: [],
  permissionsError: null,
  guestDemoLinkEncryptedEnabled: null,
  guestDemoLinkWithValidationEnabled: null,
  tenantSettings: {},
  isMaxUsersReached: false,
  openModal: "",

};
const WELCOME_MODAL_SEEN_KEY = 'welcomeModalSeen';

const hasSeenWelcomeModal = () => {
  return localStorage.getItem(WELCOME_MODAL_SEEN_KEY) === 'true';
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLogoutDialogOpen: (state, action) => {
      state.logoutDialogOpen = action.payload
    },
    loginSuccess: (state, action) => {
      state.token = action.payload;
      state.error = null;
    },
    refreshToken: (state, action) => {
      state.token = action.payload;
      state.error = null;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.token = null;
      state.error = null;
    },
    authError: (state, action) => {
      state.isAuthenticated = false;
      state.token = null;
      state.error = action.payload;
    },
    setKeycloakSettings: (state, action) => {
      state.keycloakSettings = action.payload
    },
    setTenant: (state, action) => {
      state.tenant = action.payload
    },
    setLivekitUrl: (state, action) => {
      state.livekitUrl = action.payload
    },
    updateLocalAvatar: (state, action) => {
      // Get the updated avatar from the avatars object
      const newAvatars = action.payload;
      const updatedAvatar = newAvatars[state.localAvatar.id];
      // only update updated properties without replacing the whole object
      if (updatedAvatar &&
        (updatedAvatar.id === state.localAvatar.id) &&
        (JSON.stringify(updatedAvatar) !== JSON.stringify(state.localAvatar)))
        state.localAvatar = { ...state.localAvatar, ...updatedAvatar };
    },
    setPreferredLanguage: (state, action) => {
      state.preferredLanguage = action.payload;
      localStorage.setItem('language', action.payload);
    },
    ON_GUEST_FIRED: (state, action) => {
      if (state.localAvatar?.email === action.payload.data) {
        const demoToken = localStorage.getItem('demoToken');
        console.log("guest login fired code", demoToken);
        if (demoToken) { window.location.href = `/guest/${demoToken}`; }
        else {
          const mapId = localStorage.getItem('demoMapId');
                  console.log("guest login fired mapId", mapId);

          if (mapId) { window.location.href = `/guest/demo/${mapId}`; }
          else{
            const code = localStorage.getItem('code');
            console.log("guest login fired code", code);
            if (code) {
              const isConference = localStorage.getItem('isConferenceGuest');
              if(isConference) {
                window.location.href = `/conference/guest/${code}`;
              }
              else{
              window.location.href = `/guest/${code}`;
              }
               }
          }
        }
      }
    },
    ON_DOUBLE_SOCKET: (state, action) => {
      state.doubleSession = true;
    },
    setInvitationAuthorization: (state, action) => {
      state.invitationAuthorization = action.payload
    },
    /** Après sauvegarde du modèle STT en admin : met à jour tout de suite la mention / VoiceTranscription. */
    patchTenantSttModel: (state, action) => {
      const value = action.payload;
      if (value === undefined || value === null) return;
      const next = { ...(state.tenantSettings || {}) };
      next.stt_model = value;
      bindTenantSettings(next);
      state.tenantSettings = next;
    },
    setOpenModal: (state, action) => {
      state.openModal = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.fulfilled, (state, action) => {
        if (typeof action.payload === "string" && action.payload == "MAX_USERS_REACHED") {
          state.isMaxUsersReached = true;
          console.log("action.payload", action.payload, state.isMaxUsersReached, state.isAuthenticated)
        }
        else {
          state.isAuthenticated = true;
          state.localAvatar = action.payload;
          state.error = null;
         
          //Socket factory setup
          //Staep 1 : initialize socket instance
          socketFactory.initializeSocket({
            url: config.SERVER_URL + '/tenant/' + state.tenant.id,
            options: {
              withCredentials: true,
              query: {
                email: state.localAvatar.email,
                id: state.localAvatar.id,
                tenantId: state.tenant.id,
              },
              auth: {
                token: state.token
              }
            }
          });
          if (!hasSeenWelcomeModal()) {
            state.openModal = "welcome";
          } else {
            state.openModal = ""; // Ensure it's not set if already seen
          }

        }

        // Start avatar sync 

      })

      .addCase(guestLogin.fulfilled, (state, action) => {
        if (typeof action.payload === "string" && action.payload === "max_guest_reached") {
          state.invitationAuthorization = false;
        }
        if (typeof action.payload !== "string") {
          state.token = action.payload.tokenResponse.access_token
          state.isAuthenticated = true;
          state.localAvatar = action.payload.user;
          state.error = null;
          state.tenant = action.payload.tenant
          state.isGuest = true;
          state.isGuest = true;
        
          //Socket factory setup
          //Staep 1 : initialize socket instance
          socketFactory.initializeSocket({
            url: config.SERVER_URL + '/tenant/' + state.tenant.id,
            options: {
              withCredentials: true,
              query: {
                email: state.localAvatar.email,
                id: state.localAvatar.id,
                tenantId: state.tenant.id,
              },
              auth: {
                token: state.token
              }
            }
          });
          if (!hasSeenWelcomeModal()) {
            state.openModal = "welcome";
          } else {
            state.openModal = ""; // Ensure it's not set if already seen
          }
        }

      })
      .addCase(login.rejected, (state, action) => {
        state.isAuthenticated = false;
        state.error = action.payload;
      })
      .addCase(guestLogin.rejected, (state, action) => {
        state.isAuthenticated = false;
        state.error = action.payload;
      })
      .addCase(getLivekitUrl.fulfilled, (state, action) => {
        state.livekitUrl = action.payload;
      })
      .addCase(getLivekitUrl.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(getTenantByDomain.fulfilled, (state, action) => {
        state.tenant = action.payload.tenant.id;
      })
      .addCase(getTenantByDomain.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(guestLoginDemo.fulfilled, (state, action) => {
        if (typeof action.payload === "string" && action.payload == "max_guest_reached") {
          state.invitationAuthorization = false;
        }
        if (typeof action.payload !== "string") {
          state.token = action.payload.tokenResponse.access_token
          state.localAvatar = action.payload.user;
          state.error = null;


          state.tenant = action.payload.tenant
          state.isAuthenticated = true;
          state.isGuestDemo = true;
          state.isGuest = true;
         
          //Socket factory setup
          //Staep 1 : initialize socket instance
          socketFactory.initializeSocket({
            url: config.SERVER_URL + '/tenant/' + state.tenant.id,
            options: {
              withCredentials: true,
              query: {
                email: state.localAvatar.email,
                id: state.localAvatar.id,
                tenantId: state.tenant.id,
              },
              auth: {
                token: state.token
              }
            }
          });

          if (!hasSeenWelcomeModal()) {
            state.openModal = "welcome";
          } else {
            state.openModal = ""; // Ensure it's not set if already seen
          }
        }


      })
      .addCase(guestLoginDemo.rejected, (state, action) => {
        state.isAuthenticated = false;
        state.invitationAuthorization = false
        state.error = action.payload;
      })

      .addCase(logoutGuestUser.fulfilled, (state, action) => {
        state.token = action.payload.tokenResponse.access_token
        state.localAvatar = null;
        state.error = null;
        state.tenant = null
        state.isAuthenticated = false;
        state.isGuestDemo = false;
        state.isGuest = false;
      })
      .addCase(logoutGuestUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(getTenantSettings.fulfilled, (state, action) => {
        state.tenantSettings = action.payload.tenantSettings;
        bindTenantSettings(state.tenantSettings);
      })
      .addCase(getTenantSettings.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(getUserPermissions.fulfilled, (state, action) => {
        state.permissions = Array.isArray(action.payload) ? action.payload : [];
        state.permissionsError = null;
      })
      .addCase(getUserPermissions.rejected, (state, action) => {
        state.permissionsError = action.payload || action.error?.message || 'Failed to fetch permissions';
        state.permissions = [];
      })
      .addCase(getGuestDemoLinkEncryptedSetting.fulfilled, (state, action) => {
        state.guestDemoLinkEncryptedEnabled = action.payload?.enabled ?? false;
      })
      .addCase(getGuestDemoLinkEncryptedSetting.rejected, (state) => {
        state.guestDemoLinkEncryptedEnabled = false;
      })
      .addCase(getGuestDemoLinkValidationSetting.fulfilled, (state, action) => {
        state.guestDemoLinkWithValidationEnabled = action.payload?.enabled ?? false;
      })
      .addCase(getGuestDemoLinkValidationSetting.rejected, (state) => {
        state.guestDemoLinkWithValidationEnabled = false;
      })


  },
});


export const { loginSuccess, refreshToken,
  logout,
  authError,
  setKeycloakSettings,
  setTenant,
  setLivekitUrl,
  setLogoutDialogOpen,
  updateLocalAvatar,
  setPreferredLanguage,
  ON_GUEST_FIRED,
  ON_DOUBLE_SOCKET,
  setInvitationAuthorization,
  patchTenantSttModel,
  setOpenModal
} = authSlice.actions;



socketFactory.on("guestFired", ON_GUEST_FIRED)
socketFactory.on("doubleSocket", ON_DOUBLE_SOCKET)

export default authSlice.reducer;